/**
 * if you have a idea to improve or revamp the authentication process please feel free to improve it
 *
 * this file is inspired by the microsoft provided example project "ms-identity-b2c-javascript-spa"
 * https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa/tree/main/App
 * alternative ?
 * https://github.com/Azure-Samples/ms-identity-javascript-v2/tree/master/app
 *
 * learn more here:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-sign-in?tabs=javascript2
 * https://docs.microsoft.com/de-de/azure/active-directory-b2c/quickstart-single-page-app
 */

import i18n from 'i18next'

import * as msal from '@azure/msal-browser'
import { EndSessionRequest, RedirectRequest, SilentRequest } from '@azure/msal-browser'

import { SetIdToken } from '~utils/sessionStorage'
import { signInScopes, b2cTokenRequestScopes } from './msalScopes'
import { msalConfig } from './msalAuthConfig'
import { b2cPolicies } from './msalPolicies'
import { MsalAccountInfo, MsalAuthenticationResult } from '~shared/models/msalExtendedTypes'

const msalInstance = new msal.PublicClientApplication(msalConfig)

/**
 * Login is successful when "accountId" !== undefined
 * the accountId & other data should be stored elsewhere (redux, reactContext ect.. ) - right?
 * for easy usage of "signOut" & "getTokenSilent" the active account id is stored here as well
 **/
let accountId: string | undefined

export const selectAccount: () => MsalAccountInfo | undefined = () => {
  /**
   * See here for more info on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  const currentAccounts = msalInstance.getAllAccounts()

  if (currentAccounts.length > 1) {
    /**
     * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
     * is cached as a new account, which results in more than one account in the cache. Here we make
     * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
     * as this is the default flow the user initially signed-in with.
     */
    const accounts = currentAccounts.filter(account => {
      const accountInfo: MsalAccountInfo = account
      return accountInfo.homeAccountId.toUpperCase().includes(b2cPolicies.names.signUpSignIn.toUpperCase())
        && accountInfo?.idTokenClaims?.iss?.toUpperCase().includes(b2cPolicies.authorityDomain.toUpperCase())
        && accountInfo.idTokenClaims.aud === msalConfig.auth.clientId
    })
    if (accounts.length > 1) {
      // localAccountId identifies the entity for which the token asserts information.
      if (accounts.every(account => account.localAccountId === accounts[0].localAccountId)) {
        // All accounts belong to the same user
        accountId = accounts[0].homeAccountId
        return accounts[0]
      } else {
        // Multiple users detected. Logout all to be safe.
        signOut()
      }
    } else if (accounts.length === 1) {
      accountId = accounts[0].homeAccountId
      return accounts[0]
    }
  } else if (currentAccounts.length === 1) {
    accountId = currentAccounts[0].homeAccountId
    return currentAccounts[0]
  }
}

export function signOut() {
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */

  const logoutRequest: EndSessionRequest = {
    postLogoutRedirectUri: msalConfig.auth.redirectUri,
  }

  msalInstance.logoutRedirect(logoutRequest)
  return true
}

export const signInRedirect = () => {
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */

  return msalInstance.loginRedirect({
    scopes: [ // for more information about scopes see "signInScopes" in authConfig
      'openid',
      'profile',
      'email',
      ...signInScopes,
    ],
  })
    .then(() => {
      return true
    })
    .catch((e) => {
      console.log('loginRedirect failed:', e)
      alert(i18n.t('error:signInError'))
      return false
    })
}

export enum RedirectResponseStatus {
  success = 'success',
  fail = 'fail',
  resetPassword = 'resetPassword',
  profileUpdateSuccess = 'profileUpdateSuccess',
  passwordResetSuccess = 'passwordResetSuccess',
}

interface SignInRedirectPromiseReturn {
  status: RedirectResponseStatus
  accountInfo?: msal.AccountInfo
}

export const handleRedirectPromiseAndSelectAccount: () => Promise<SignInRedirectPromiseReturn> = () => {
  return msalInstance.handleRedirectPromise()
    .then(response => {
      let accountInfo
      const typecastResponse = response as MsalAuthenticationResult | undefined
      if (typecastResponse?.idTokenClaims.tfp || typecastResponse?.idTokenClaims.acr) {
        /**
         * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
         * from SUSI flow. "tfp" claim in the id token tells us the policy (NOTE: legacy policies may use "acr" instead of "tfp").
         * logout when editProfile OR forgotPassword policies are found
         * tfp = Trust framework policy -- ( policies 2.0 )
         * acr = Azure Container Registry -- ( policies 1.0 )
         *
         * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
         */
        const tfpOrAcr = typecastResponse.idTokenClaims.tfp
          ? typecastResponse.idTokenClaims.tfp.toLowerCase()
          : typecastResponse.idTokenClaims.acr.toLowerCase()
        switch (tfpOrAcr) {
          case b2cPolicies.names.signUpSignIn.toLowerCase():
            accountInfo = handleSignInResponse(typecastResponse)
            break

          /**
           * it is not clear that it is still required to logout after editProfile & forgotPassword
           * in the example repo it was removed without a comment:
           * https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa/commit/e52e37b7ca29de20cb8187d236439b57c0c8346c#diff-c0a5c80d0d29e6cd55220d6279e04b6fe1f38f3448b5852075f00434a8724cc3
           * better safe than sorry
           */
          case b2cPolicies.names.editProfile.toLowerCase():
            /**
             * // alert(i18n.t('common:profileUpdateSuccess'))
             * // signOut() // required!
             * handled outside of msalAuthentication.ts
             * important -> show popup until 'ok' is clicked and then signOut - restrict user to only be able to click 'ok'
             */
            return {
              status: RedirectResponseStatus.profileUpdateSuccess,
            }
          case b2cPolicies.names.forgotPassword.toLowerCase():
            /**
             * // alert(i18n.t('common:profileUpdateSuccess'))
             * // signOut() // required!
             * handled outside of msalAuthentication.ts
             * important -> show popup until 'ok' is clicked and then signOut - restrict user to only be able to click 'ok'
             */
            return {
              status: RedirectResponseStatus.passwordResetSuccess,
            }
        }
      }

      if (!accountInfo) accountInfo = selectAccount()

      return {
        status: accountInfo ? RedirectResponseStatus.success : RedirectResponseStatus.fail,
        accountInfo: accountInfo,
      }
    })
    .catch(error => {
      console.log('handleRedirectPromise error:', error)
      if (error.errorMessage) {
        // handle forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (error.errorMessage.indexOf('AADB2C90118') > -1) {
          msalInstance.loginRedirect({
            ...b2cPolicies.authorities.forgotPassword,
            scopes: [],
          })
            .catch(() => {
              console.log('loginRedirect error on forgotPassword redirect')
              signOut()
            })

          return {
            status: RedirectResponseStatus.resetPassword,
          }
        }
      }

      return {
        status: RedirectResponseStatus.fail,
      }
    })
}

const handleSignInResponse: (response: MsalAuthenticationResult) => MsalAccountInfo | undefined = (response) => {
  /**
   * To see the full list of response object properties, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
   */

  if (response.account?.homeAccountId) {
    accountId = response.account.homeAccountId
  } else {
    console.error('signin accountId is undefined', accountId)
    signOut()
  }

  // if response contains an idToken, store it
  if (response.idToken && response.idToken !== '') {
    SetIdToken(response.idToken)
  }

  return selectAccount()
}

/**
 * To initiate a B2C user-flow, simply make a login request using
 * the full authority string of that user-flow e.g.
 * https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/B2C_1_edit_profile_v2
 */
export function editProfile() {
  const editProfileRequest: RedirectRequest = {
    ...b2cPolicies.authorities.editProfile,
    scopes: [],
    loginHint: accountId ? msalInstance.getAccountByHomeId(accountId)?.username : undefined,
  }

  msalInstance.loginRedirect(editProfileRequest)
}

/**
 * initiate a redirect forgot password
 */
export function forgotPassword() {
  const forgotPasswordRequest: RedirectRequest = {
    ...b2cPolicies.authorities.forgotPassword,
    scopes: [],
    loginHint: accountId ? msalInstance.getAccountByHomeId(accountId)?.username : undefined,
  }

  msalInstance.loginRedirect(forgotPasswordRequest)
}

export const getTokenSilentRedirect = () => {
  /**
   * See here for more info on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  const acquireTokenRequestProps: SilentRequest = {
    account: (accountId && msalInstance.getAccountByHomeId(accountId)) || undefined,
    scopes: [...b2cTokenRequestScopes], // see "msalScopes" for more info
    // todo - has session id or login_hint has to be send for this to function properly ?
    // https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-sso#sso-without-msaljs-login
    // https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-sso
    // s: msalInstance.getSessionId(accountId),
    // redirectUri: todo test when form data is filled -> and redirect back to curde / from / wherever
    forceRefresh: false, // Set this to "true" to skip a cached token and go to the server to get a new token
  }

  /**
   * acquireTokenSilent will examine expiration, you do not need to build logic around this.
   * When you call acquireTokenSilent it will first attempt to retrieve the access token from the cache.
   * If it is expired or within the offset (5 minutes by default) or cannot be found it will then attempt
   * to exchange the refresh token for a new access token. It will make a network call, at most, once per hour.
   */
  return msalInstance.acquireTokenSilent(acquireTokenRequestProps)
    .then((response) => {
      // In case the response from B2C server has an empty idToken field
      // throw an error to initiate token acquisition
      if (!response.idToken || response.idToken === '') {
        console.log('idToken token missing in response')
        throw new msal.InteractionRequiredAuthError()
      } else {
        SetIdToken(response.idToken)
        return true
      }
    }).catch(error => {
      console.log('Silent token acquisition fails. Acquiring token using popup. \n', error)
      if (error instanceof msal.InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        // todo - if there is anything that needs to be saved somehow before the redirect - we have to improve this implementation
        // improvement proposal - use msal.acquireTokenPopup to not lose unsaved data
        //                        we would have to use a overlay which blocks other interactions while doing acquireTokenPopup as well
        msalInstance.acquireTokenRedirect(acquireTokenRequestProps)
      } else {
        console.log('unknown error in "acquireTokenSilent"', error)
        signOut()
      }
      return false
    })
}
